import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Lock, Phone, MessageCircle, Trash2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/site";
import { LogoMark } from "@/components/site/Logo";

const http = axios.create({ baseURL: API, withCredentials: true });
const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).join(" ");
  return "Something went wrong. Please try again.";
};

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { const { data } = await http.post("/auth/login", { email, password }); onDone(data); }
    catch (err) { setError(errText(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-5 pt-24">
      <form onSubmit={submit} data-testid="admin-login-form" className="glass w-full max-w-sm rounded-3xl p-8">
        <LogoMark className="h-12 w-12" />
        <h1 className="font-display mt-6 text-2xl font-semibold text-white">Admin sign in</h1>
        <p className="mt-1 text-sm text-slate-400">View and manage website enquiries.</p>
        <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="mt-8 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]" />
        <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]" />
        {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">{error}</p>}
        <button data-testid="admin-login-submit" disabled={loading} className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
      </form>
    </div>
  );
};

const STATUS_STYLE = { new: "bg-gold/15 text-[var(--gold-2)]", contacted: "bg-royal/20 text-blue-300", closed: "bg-white/10 text-slate-300" };

const Row = ({ e, onStatus, onDelete }) => (
  <article data-testid={`admin-enquiry-${e.id}`} className="rounded-2xl border border-white/[0.08] bg-navy/60 p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-semibold text-white">{e.full_name}</p>
        <p className="text-sm text-slate-400">{e.business_name || "—"} · {new Date(e.created_at).toLocaleString("en-IN")}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${e.email_status === "sent" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>email {e.email_status}</span>
        <select data-testid={`admin-status-select-${e.id}`} value={e.status} onChange={(ev) => onStatus(e.id, ev.target.value)} className={`rounded-full border-0 bg-transparent px-3 py-1 text-xs font-semibold outline-none ${STATUS_STYLE[e.status]}`}>
          {["new", "contacted", "closed"].map((s) => <option key={s} value={s} className="bg-[#0a1226] text-white">{s}</option>)}
        </select>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2 text-xs">
      <span className="rounded-full bg-white/5 px-3 py-1 text-slate-200">{e.website_type}</span>
      <span className="rounded-full bg-white/5 px-3 py-1 text-slate-200">{e.budget_range}</span>
      <span className="rounded-full bg-white/5 px-3 py-1 text-slate-200">{e.email}</span>
      <span className="rounded-full bg-white/5 px-3 py-1 text-slate-200">{e.mobile}</span>
    </div>
    <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-300">{e.requirements}</p>
    <div className="mt-5 flex flex-wrap gap-2">
      <a href={`tel:+91${e.mobile}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white hover:border-[var(--gold)]"><Phone className="h-3.5 w-3.5" /> Call</a>
      <a href={`https://wa.me/91${e.mobile}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-black"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
      <button data-testid={`admin-delete-${e.id}`} onClick={() => onDelete(e.id)} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-4 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
    </div>
  </article>
);

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await http.get("/admin/enquiries"); setItems(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(errText(e)); if (e.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { await http.patch(`/admin/enquiries/${id}`, { status }); setItems((p) => p.map((x) => (x.id === id ? { ...x, status } : x))); toast.success("Status updated"); }
    catch (e) { toast.error(errText(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await http.delete(`/admin/enquiries/${id}`); setItems((p) => p.filter((x) => x.id !== id)); toast.success("Enquiry deleted"); }
    catch (e) { toast.error(errText(e)); }
  };

  const shown = useMemo(() => items.filter((e) => (filter === "all" || e.status === filter) &&
    `${e.full_name} ${e.email} ${e.mobile} ${e.business_name} ${e.website_type}`.toLowerCase().includes(q.toLowerCase())), [items, q, filter]);

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[var(--gold)]">Admin</p>
          <h1 className="font-display mt-2 text-3xl font-semibold text-white">Website enquiries</h1>
          <p className="mt-1 text-sm text-slate-400">Signed in as {user.email} · {items.length} total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="admin-refresh-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          <button onClick={onLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white"><LogOut className="h-3.5 w-3.5" /> Logout</button>
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <label className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, mobile, type" className="w-full rounded-full border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none focus:border-[var(--gold)]" />
        </label>
        <select data-testid="admin-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none">
          {["all", "new", "contacted", "closed"].map((s) => <option key={s} value={s} className="bg-[#0a1226]">{s === "all" ? "All statuses" : s}</option>)}
        </select>
      </div>
      <div className="mt-8 space-y-4">
        {loading && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--gold)]" />}
        {!loading && shown.length === 0 && <p data-testid="admin-empty" className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-slate-400">No enquiries yet.</p>}
        {!loading && shown.map((e) => <Row key={e.id} e={e} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
    </main>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    document.title = "Admin | Pratap Group";
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = useCallback(async () => { try { await http.post("/auth/logout"); } catch { /* ignore */ } setUser(false); }, []);

  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" /></div>;
  if (!user) return <Login onDone={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
